import { useEffect, useState } from "react";

// Hebrew news headlines · read from the shared cache, never translated here.
//
// Since 26/10/2026 the SERVER translates: the news proxy hands out each
// article with its Hebrew from the cache (`titleHe`, `excerptHe` ·
// utils/finnhubNews.js) and passes what is missing to a background run with
// 15 minutes to finish (netlify/functions/lib/newsHebrew.js). The browser
// used to ask Gemini through `summarize` when a headline reached the screen,
// with 10 seconds to answer; on 26/10 Gemini took 21–33 seconds and every
// such call failed, leaving English on screen and nothing saved.
//
// What is left here is the lookup: an article that arrived without its
// Hebrew is asked for again from the cache (one batched call, no Gemini), so
// the background run's answer reaches a screen that is already open.
//
// The rule (26/10/2026): no English headline in the app. An article without
// a Hebrew headline is not shown until it has one (useHebrewNews below).

export function isHebrew(text) {
  return /[֐-׿]/.test(text || "");
}

export const BATCH_MAX = 30;          // a cache lookup
export const SEND_MAX = BATCH_MAX;
const BATCH_WINDOW_MS = 120;
const REQUEST_MS = 9000;
// A placeholder waits a little longer than a lookup may take.
export const TRANSLATING_MS = REQUEST_MS + 1500;

const memo = new Map();       // article key → { title, summary }, each Hebrew or null
const failedAt = new Map();   // article key → when the cache did not have it
// A miss is asked again after this long: the background run is translating it.
export const RETRY_MS = 20 * 1000;
let queue = [];               // [{ id, title, resolve }]
let timer = null;

// For tests: the transport, and the session memory.
let transport = async (items) => {
  const res = await fetch("/.netlify/functions/summarize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode: "titles", items, cacheOnly: true }),
    signal: AbortSignal.timeout(REQUEST_MS),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};
export function _setTransport(fn) { transport = fn; }
export function _reset() { memo.clear(); failedAt.clear(); queue = []; clearTimeout(timer); timer = null; }

async function flush() {
  timer = null;
  const batch = queue.splice(0, SEND_MAX);
  if (queue.length) timer = setTimeout(flush, 0);
  if (!batch.length) return;
  const items = [...new Map(batch.map(b => [b.key, b.summary ? { id: b.id, title: b.title, summary: b.summary } : { id: b.id, title: b.title }])).values()];
  let titles = {}, summaries = {};
  try {
    ({ titles = {}, summaries = {} } = (await transport(items, { cacheOnly: true })) || {});
  } catch { titles = {}; summaries = {}; }
  const clean = v => (typeof v === "string" && v.trim() ? v.trim() : null);
  for (const b of batch) {
    const out = { title: clean(titles[b.id]), summary: b.summary ? clean(summaries[b.id]) : null };
    memo.set(b.key, out);
    // A miss is remembered for RETRY_MS only, so the next ask reaches the
    // cache again once the background run has saved the translation.
    if (!out.title) failedAt.set(b.key, Date.now()); else failedAt.delete(b.key);
    b.resolve(out);
  }
}

function request(id, title, summary = "") {
  const key = summary ? `${id}~s` : id;
  const failed = failedAt.get(key);
  if (failed && Date.now() - failed >= RETRY_MS) { memo.delete(key); failedAt.delete(key); }
  if (memo.has(key)) return Promise.resolve(memo.get(key));
  return new Promise(resolve => {
    queue.push({ key, id, title, summary, resolve });
    if (queue.length >= SEND_MAX) { clearTimeout(timer); flush(); }
    else if (!timer) timer = setTimeout(flush, BATCH_WINDOW_MS);
  });
}

const keyOf = (article, t) => String(article.id || article.url || t).slice(0, 200);

// Best Hebrew headline for an article. Never throws; resolves to the Hebrew
// headline, or to the original when none is available.
export async function hebrewHeadline(article) {
  const t = (article && article.title) || "";
  if (!t || isHebrew(t)) return t;
  if (isHebrew(article.titleHe)) return article.titleHe;
  const { title } = await request(keyOf(article, t), t);
  return title || t;
}

// Company news (2026-09-26): the headline AND its summary, in one lookup. `summary` is the Hebrew summary, or null when
// none is available · the caller then HIDES the English summary rather than
// showing it under a Hebrew headline.
export async function hebrewArticle(article) {
  const t = (article && article.title) || "";
  const ex = ((article && article.excerpt) || "").trim();
  const summaryOut = ex && isHebrew(ex) ? ex : null;
  if (!t) return { title: t, summary: summaryOut };
  if (isHebrew(t) && (!ex || isHebrew(ex))) return { title: t, summary: summaryOut };
  // The proxy's Hebrew (26/10/2026): complete when the summary came with it.
  const exHe = isHebrew(article.excerptHe) ? article.excerptHe : null;
  if (isHebrew(article.titleHe) && (!ex || summaryOut || exHe)) return { title: article.titleHe, summary: summaryOut || exHe };
  const r = await request(keyOf(article, t), t, ex && !isHebrew(ex) ? cutSummary(ex) : "");
  return { title: isHebrew(t) ? t : (r.title || (isHebrew(article.titleHe) ? article.titleHe : t)), summary: summaryOut || r.summary || exHe || null };
}

// ─── A summary, cut where a sentence ends (sprint 13, 1.2) ───────────────────
// At most 400 characters, ending at the last whole sentence that fits · never
// in the middle of a word. With no sentence end inside the limit, at the last
// space. Same rule as the server (netlify/functions/summarize.js).
export const SUMMARY_MAX = 400;
export function cutSummary(text, max = SUMMARY_MAX) {
  const s = String(text || "").trim();
  if (s.length <= max) return s;
  const head = s.slice(0, max);
  let end = -1;
  const re = /[.!?](?=\s|$)/g;
  let m;
  while ((m = re.exec(head))) end = m.index + 1;
  if (end >= 40) return head.slice(0, end).trim();
  const sp = head.lastIndexOf(" ");
  return (sp > 0 ? head.slice(0, sp) : head).trim();
}

// ─── What is already translated (sprint 13, 1.1) ─────────────────────────────
// The server translates the feed every hour (news-translate-scheduled.js).
// This asks for those translations only · a cache lookup, no Gemini call · so
// a card shows Hebrew the moment it is on screen. A hit is remembered like any
// other answer; a miss is not, so the card's "תרגם" still translates it.
// Resolves to { key → { title, summary } } for the hits.
export async function peekArticles(articles) {
  const out = {};
  const ask = [];
  for (const a of articles || []) {
    const t = (a && a.title) || "";
    if (!t || isHebrew(t)) continue;
    const ex = ((a && a.excerpt) || "").trim();
    const summary = ex && !isHebrew(ex) ? cutSummary(ex) : "";
    const key = summary ? `${keyOf(a, t)}~s` : keyOf(a, t);
    // What the proxy already brought (26/10/2026) needs no lookup.
    if (isHebrew(a.titleHe) && (!summary || isHebrew(a.excerptHe))) { out[keyOf(a, t)] = { title: a.titleHe, summary: summary ? a.excerptHe : null }; continue; }
    // A remembered failure is not a hit · the card keeps its "תרגם".
    if (memo.has(key) && memo.get(key).title) { out[keyOf(a, t)] = memo.get(key); continue; }
    if (memo.has(key)) continue;
    ask.push({ a, t, summary, key });
  }
  for (let i = 0; i < ask.length; i += BATCH_MAX) {
    const chunk = ask.slice(i, i + BATCH_MAX);
    let titles = {}, summaries = {};
    try {
      ({ titles = {}, summaries = {} } = (await transport(
        chunk.map(c => (c.summary ? { id: keyOf(c.a, c.t), title: c.t, summary: c.summary } : { id: keyOf(c.a, c.t), title: c.t })),
        { cacheOnly: true },
      )) || {});
    } catch { continue; }
    const clean = v => (typeof v === "string" && v.trim() ? v.trim() : null);
    for (const c of chunk) {
      const id = keyOf(c.a, c.t);
      const hit = { title: clean(titles[id]), summary: c.summary ? clean(summaries[id]) : null };
      // Only a complete hit is remembered: a headline without its summary is
      // still worth showing, but the summary may yet be translated.
      if (hit.title) {
        out[id] = hit;
        if (!c.summary || hit.summary) memo.set(c.key, hit);
      }
    }
  }
  return out;
}

// ─── Only what is in Hebrew (26/10/2026) ─────────────────────────────────────
// `articles` → { he, missing }. `he[key]` is { title, summary } in Hebrew for
// every article that has its Hebrew headline (the summary may be null: then
// no summary line, never an English one). An article without it is not in
// `he` and is counted in `missing`; it is asked for again from the cache
// at ASK_AGAIN_MS, while the background run translates it, and joins the
// list when it arrives. `ready` is false until the first lookup has answered,
// so a list does not first draw itself without the articles on their way.
const ASK_AGAIN_MS = [25000, 70000, 150000, 300000];
const EMPTY = Object.freeze({});   // one object, so a memo built on `he` holds until the answer
export function useHebrewNews(articles) {
  const list = Array.isArray(articles) ? articles : [];
  const sig = list.map(a => a && (a.id || a.url)).join("|");
  const [state, setState] = useState({ sig: null, he: {} });

  useEffect(() => {
    let alive = true;
    const timers = [];
    const found = {};
    const want = list.filter(a => a && a.title);
    const ask = () => Promise.all(want.filter(a => !found[a.id || a.url]).map(a => hebrewArticle(a).then(r => {
      if (isHebrew(r.title)) found[a.id || a.url] = { title: r.title, summary: isHebrew(r.summary) ? r.summary : null };
    }))).then(() => { if (alive) setState({ sig, he: { ...found } }); });
    const again = () => { if (alive && want.some(a => !found[a.id || a.url])) ask(); };
    ask().then(() => { ASK_AGAIN_MS.forEach(ms => timers.push(setTimeout(again, ms))); });
    return () => { alive = false; timers.forEach(clearTimeout); };
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = state.sig === sig;
  const he = ready ? state.he : EMPTY;
  return { he, ready, missing: ready ? list.filter(a => a && a.title && !he[a.id || a.url]).length : 0 };
}

// The line a list shows for what is held back, or null.
export function waitingNote(n) {
  if (!(n > 0)) return null;
  return n === 1 ? "כתבה נוספת ממתינה לתרגום ותוצג כשתתורגם" : `עוד ${n} כתבות ממתינות לתרגום ויוצגו כשיתורגמו`;
}
